import { DataButton } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useAvailabilityListStore } from "./availabilityListStore";
import { ContainerRuleSelection } from "./availabilityListStore";
import { Icon } from "../../components";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../components/dropdown/Dropdown";
import { capitalize } from "@sellar-io/common";
import type { FulfillmentTypeListResponseType } from "@sellar-io/api-v2/types";

export const AvailabilityListFilter = ({
  onDismiss,
  setClickOutside,
  deliveryMethods,
  hasSellarDistributionOption,
}: {
  onDismiss: () => void;
  setClickOutside?: (value?: boolean) => void;
  deliveryMethods: FulfillmentTypeListResponseType;
  hasSellarDistributionOption: boolean;
}) => {
  const state = useAvailabilityListStore(useShallow((state) => state));

  const [productVisibility, setProductVisibility] = useState(
    state.productVisibility,
  );
  const [containerRuleSelection, setContainerRuleSelection] =
    useState<ContainerRuleSelection>(state.containerRuleSelection);

  const containerRulesLabel = (() => {
    if (containerRuleSelection.type === "none") return "None";
    if (containerRuleSelection.type === "sellarDistribution") {
      return "Sellar distribution";
    }

    const selectedDeliveryMethod = deliveryMethods.find(
      (method) => method.id === containerRuleSelection.id,
    );

    return (
      selectedDeliveryMethod?.name ??
      capitalize(selectedDeliveryMethod?.fulfillmentType ?? "")
    );
  })();

  const filtersAlreadyApplied =
    state.productVisibility !== "all" ||
    state.containerRuleSelection.type !== "none";

  const disabled =
    productVisibility === state.productVisibility &&
    containerRuleSelection.type === state.containerRuleSelection.type &&
    (containerRuleSelection.type !== "deliveryMethod" ||
      (state.containerRuleSelection.type === "deliveryMethod" &&
        containerRuleSelection.id === state.containerRuleSelection.id));

  const onApply = (reset: boolean) => {
    if (reset) {
      useAvailabilityListStore.getState().reset();
    } else {
      useAvailabilityListStore.getState().setState({
        ...state,
        productVisibility,
        containerRuleSelection,
      });
    }
    if (setClickOutside) setClickOutside(true);
    onDismiss();
  };

  return (
    <>
      <div className={twMerge("w-full px-4 pt-2")}>
        <span className="font-semibold">Filter product catalogues</span>
      </div>
      <div className="w-full p-4">
        <div className="flex flex-col gap-3">
          <div className="flex w-full items-start justify-between">
            <span className="mt-2">Container rules</span>
            <div className="ml-4 flex w-[55%] flex-col gap-2">
              <Dropdown>
                <DropdownButton>
                  <DataButton
                    border={true}
                    className="relative flex w-full items-center justify-start gap-1 overflow-hidden pr-8"
                    onClick={() => setClickOutside?.(false)}
                  >
                    <span>{containerRulesLabel}</span>
                    <Icon
                      icon="CHEVRON_DOWN"
                      size={0.7}
                      className="absolute right-2"
                    />
                  </DataButton>
                </DropdownButton>
                <DropdownItems>
                  <DropdownItem
                    className="border-b border-zinc-200"
                    onClick={() => setContainerRuleSelection({ type: "none" })}
                  >
                    None
                  </DropdownItem>
                  {deliveryMethods.map((method) => (
                    <DropdownItem
                      key={method.id}
                      className="border-b border-zinc-200 last:border-b-0"
                      onClick={() =>
                        setContainerRuleSelection({
                          type: "deliveryMethod",
                          id: method.id,
                        })
                      }
                    >
                      {method.name ?? capitalize(method.fulfillmentType)}
                    </DropdownItem>
                  ))}
                  {hasSellarDistributionOption && (
                    <DropdownItem
                      className="border-b border-zinc-200 last:border-b-0"
                      onClick={() =>
                        setContainerRuleSelection({
                          type: "sellarDistribution",
                        })
                      }
                    >
                      Sellar distribution
                    </DropdownItem>
                  )}
                </DropdownItems>
              </Dropdown>
            </div>
          </div>

          <div className="flex w-full items-start justify-between">
            <span className="mt-2">Products</span>
            <div className="ml-4 flex w-[55%] flex-col gap-2">
              <Dropdown>
                <DropdownButton>
                  <DataButton
                    border={true}
                    className="relative flex w-full items-center justify-start gap-1 overflow-hidden pr-8"
                    onClick={() => setClickOutside?.(false)}
                  >
                    <span>{capitalize(productVisibility)}</span>
                    <Icon
                      icon="CHEVRON_DOWN"
                      size={0.7}
                      className="absolute right-2"
                    />
                  </DataButton>
                </DropdownButton>
                <DropdownItems>
                  <DropdownItem
                    className="border-b border-zinc-200"
                    onClick={() => setProductVisibility("all")}
                  >
                    All
                  </DropdownItem>
                  <DropdownItem
                    className="border-b border-zinc-200"
                    onClick={() => setProductVisibility("active")}
                  >
                    Active only
                  </DropdownItem>
                  <DropdownItem
                    className="border-b border-zinc-200"
                    onClick={() => setProductVisibility("inactive")}
                  >
                    Inactive only
                  </DropdownItem>
                </DropdownItems>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full items-center p-4">
        <DataButton
          onClick={() => onApply(false)}
          border={true}
          disabled={disabled}
        >
          {filtersAlreadyApplied ? "Update" : "Apply"}
        </DataButton>
        {filtersAlreadyApplied && (
          <DataButton color="transparent" onClick={() => onApply(true)}>
            Clear all
          </DataButton>
        )}
      </div>
    </>
  );
};
